// Stores
import { chart, form } from "../stores";

// Utils
import { forms } from "./forms/forms";

// Interfaces
import { CookieOptions } from "../types/CookieOptions";
import { Field } from "../types/Field";
import { IRSItem } from "../types/IRSItem";
import { RangeItem } from "../types/RangeItem";
import { __ } from "@wordpress/i18n";

// Scroll helpers
import isArticlePage from "../shared/components/IsArticlePage";
import isPage from "../shared/components/IsPage";
import { isMobileViewport } from "../shared/helpers/autoBehavior";

export const utils = {
  generateInitialValues(fields: Field[]): Record<string, any> {
    const initialValues: Record<string, any> = {};
    fields.forEach((field) => {
      const { name, defaultValue, type, icon } = field;

      // If it's a group, recursively generate and merge values
      if (type === "group" && Array.isArray(field.group_fields)) {
        const groupValues = this.generateInitialValues(field.group_fields);
        Object.assign(initialValues, groupValues); // Merge group fields into main object
      }

      if (!name) return;

      // Set default value if exists
      initialValues[name] = defaultValue !== undefined ? defaultValue : "";

      // If number
      if (
        type === "number" &&
        icon === "currency" &&
        defaultValue !== undefined
      ) {
        initialValues[name] = Number(defaultValue).toFixed(2);
      }

      // Check if field is a map and add lat and lon fields
      if (type === "map") {
        initialValues[field.latField ?? name + "_lat"] = "";
        initialValues[field.lonField ?? name + "_lon"] = "";
      }
    });
    return initialValues;
  },

  generateChartLegends: (
    data: Record<string, any>,
  ): Array<{ name: string; value: any; color: string }> => {
    const fields = data;

    // Generate list
    const list: Array<{ name: string; value: any; color: string }> = [];
    const entries = Object.entries(fields);
    entries.forEach(([key, originalValue], index) => {
      let color,
        name = key;
      let value = originalValue;

      // Check if originalValue is an object
      if (typeof originalValue === "object") {
        value = originalValue.value;
        color = originalValue.color || originalValue.backgroundColor;
        name = originalValue.name;
      }

      // Get field name from submitted form fields
      const findField = forms[form()?.type]?.fields.find(
        (obj) => obj.name === key,
      );
      if (findField) {
        name = findField.label;
      }

      color = color || chart().colors[index];

      // Add to list
      list.push({ name, value, color });
    });

    return list;
  },

  updateUrl: (values: Record<string, any>): void => {
    // Remove empty values
    Object.keys(values).forEach((key) => {
      if (values[key] === "") {
        delete values[key];
      }
    });

    // Convert to Base64 and add it to url
    const encoded = btoa(JSON.stringify(values));
    const url = window.location.href.split("?")[0];
    window.history.replaceState({}, "", `${url}?data=${encoded}`);
  },

  toEuro: (number: number, precision: number | null = null): string => {
    return utils
      .toRound(number, precision, {
        currency: "EUR",
        style: "currency",
        currencyDisplay: "symbol",
      })
      .replace(/\u00A0/g, "");
  },

  toRound: (
    number: number,
    precision: number | null = null,
    args: object = {},
  ): string => {
    const defaultPrecision = 2;
    return number.toLocaleString("de-DE", {
      ...args,
      minimumFractionDigits:
        precision == null
          ? number % 1 !== 0
            ? defaultPrecision
            : 0
          : precision,
      maximumFractionDigits: precision ? precision : defaultPrecision,
    });
  },

  transformDate: (
    inputDate: string,
    minimal: boolean,
    justName: boolean,
    separator = "/",
  ): string => {
    let month, year;
    if (separator == "/") {
      [month, year] = inputDate.split("/");
    } else {
      [year, month] = inputDate.split("-");
    }

    const monthNames = [
      __("Janeiro", "df-simulators"),
      __("Fevereiro", "df-simulators"),
      __("Março", "df-simulators"),
      __("Abril", "df-simulators"),
      __("Maio", "df-simulators"),
      __("Junho", "df-simulators"),
      __("Julho", "df-simulators"),
      __("Agosto", "df-simulators"),
      __("Setembro", "df-simulators"),
      __("Outubro", "df-simulators"),
      __("Novembro", "df-simulators"),
      __("Dezembro", "df-simulators"),
    ];
    const monthAbbreviations = [
      __("Jan", "df-simulators"),
      __("Fev", "df-simulators"),
      __("Mar", "df-simulators"),
      __("Abr", "df-simulators"),
      __("Mai", "df-simulators"),
      __("Jun", "df-simulators"),
      __("Jul", "df-simulators"),
      __("Ago", "df-simulators"),
      __("Set", "df-simulators"),
      __("Out", "df-simulators"),
      __("Nov", "df-simulators"),
      __("Dez", "df-simulators"),
    ];
    const monthAbbreviation = monthAbbreviations[parseInt(month, 10) - 1];

    if (justName) {
      return monthNames[parseInt(month, 10) - 1];
    }

    return minimal
      ? `${monthAbbreviation}/${year}`
      : `${monthNames[parseInt(month, 10) - 1]} de ${year}`;
  },

  generateIrsData: (data: IRSItem[]): Array<Record<string, any>> => {
    return data.map((item) => ({
      value: { value: item.value, type: "currency" },
      tax_max: { value: item.tax_max, type: "percentage" },
      parcel: { value: item.parcel, type: "currency" },
      tax_monthly: { value: item.tax_monthly, type: "percentage" },
    }));
  },

  generateRangeData: (
    tableData: RangeItem[],
    currentValue?: number,
  ): Array<any> => {
    const total = tableData.length;
    let lastValue = tableData[0].value.value;

    return tableData.map((item, index) => {
      let shouldBeMarked = false;
      if (currentValue !== undefined) {
        shouldBeMarked =
          currentValue >= lastValue && currentValue < item.value.value;
      }

      lastValue = item.value.value;

      const generateFirstValue = () => {
        let text = "";
        const previousValues = tableData[index - 1];
        if (previousValues && index + 1 < total) {
          text += "de " + utils.toEuro(previousValues.value.value) + " ";
        }
        text += index + 1 < total ? "até " : "mais de ";
        return text + utils.toEuro(item.value.value);
      };

      const data = [
        {
          value: generateFirstValue(),
          className: shouldBeMarked ? "bg-blue-10" : "",
        },
      ];

      Object.keys(item).forEach((key) => {
        if (key !== "value") {
          let value = item[key].value;

          if (item[key].type === "currency") {
            value =
              typeof item[key].value === "string"
                ? item[key].value
                : utils.toEuro(item[key].value);
          } else if (item[key].type === "percentage") {
            if (item[key].value === -1) {
              value = "n.a.";
            } else if (String(value).slice(-1) !== "%") {
              value += "%";
            }
          }

          data.push({
            value,
            className: shouldBeMarked ? "bg-blue-10" : "",
          });
        }
      });

      return data;
    });
  },

  cookie: {
    set({ name, value, days }: CookieOptions): void {
      let expires = "";
      if (days) {
        const date = new Date();
        date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
        expires = `; expires=${date.toUTCString()}`;
      }
      const secure = window.location.protocol === "https:" ? "; Secure" : "";
      const sameSite = "; SameSite=Lax";
      document.cookie = `${name}=${value || ""}${expires}; path=/ ${secure} ${sameSite}`;
    },

    get(name: string): string | null {
      const nameEQ = `${name}=`;
      const ca = document.cookie.split(";");
      for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) === " ") c = c.substring(1, c.length);
        if (c.indexOf(nameEQ) === 0)
          return c.substring(nameEQ.length, c.length);
      }
      return null;
    },

    delete(name: string): void {
      document.cookie = `${name}=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
    },
  },

  chartConfig: {
    cutout: "64%",
  },

  /**
   * Detects whether the current tool-block instance is embedded inside the
   * theme's "mini-tool" layout
   * Same shape as isArticlePage/isPage: a generic, runtime embedding-context
   * check, not specific to any one tool.
   */
  isMiniToolBlock: (): boolean =>
    !!document
      .querySelector('[data-block="tool-block"]')
      ?.closest("[data-hide-border]"),

  /**
   * Used by OnLoadScrollDesktop: skip on article/page contexts and when
   * embedded inside the theme's mini-tool layout (a small widget alongside
   * other marketing content — auto-scrolling the whole page for it isn't
   * wanted, regardless of device). On mobile, only auto-scroll when the
   * visitor actually clicked submit themselves.
   */
  shouldAutoScrollToToolBlock: (userSubmitted = false): boolean =>
    !isArticlePage() &&
    !isPage() &&
    !utils.isMiniToolBlock() &&
    (!isMobileViewport() || userSubmitted),

  /**
   * Finds the results wrapper, preferring the inner `__result` element
   * and falling back to the always-present outer `__results` container.
   */
  resolveResultsTarget: (): HTMLElement | null =>
    document.querySelector<HTMLElement>(
      ".tool-block__component__results__result",
    ) || document.querySelector<HTMLElement>(".tool-block__component__results"),
};
